import React, { useState } from "react"

import { Loader2 } from "lucide-react"
import { Link, Navigate } from "react-router-dom"

import AuthCard, { StaffEmailNote } from "@/components/AuthCard"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MIN_PASSWORD_LENGTH, isStaffEmail, useAuth } from "@/contexts/AuthContext"

/** Partner sign-up: an admin adds the email first; the server decides whether it may sign up. */
const Signup: React.FC = () => {
  const { user, loading, signUp } = useAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (loading) return null
  if (user) return <Navigate to="/collections/monitoring" replace />

  const staff = isStaffEmail(email)
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH
  const mismatch = confirm.length > 0 && confirm !== password
  const passwordType = showPassword ? "text" : "password"

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (staff || password.length < MIN_PASSWORD_LENGTH || confirm !== password) return
    setSubmitting(true)
    setError(null)
    try {
      await signUp({ email: email.trim(), password })
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "We couldn't create your account.")
      setSubmitting(false)
    }
  }

  return (
    <AuthCard title="Sign up">
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <div>
          <Label htmlFor="signup-email">Email address</Label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="username"
            placeholder="name@host.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1"
          />
          {staff && <StaffEmailNote />}
        </div>
        <div>
          <Label htmlFor="signup-password">Password</Label>
          <Input
            id="signup-password"
            type={passwordType}
            autoComplete="new-password"
            placeholder="Enter password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={tooShort}
            className="mt-1"
          />
          {tooShort && (
            <p className="mt-1 text-xs text-red-600">At least {MIN_PASSWORD_LENGTH} characters.</p>
          )}
        </div>
        <div>
          <Label htmlFor="signup-confirm">Confirm password</Label>
          <Input
            id="signup-confirm"
            type={passwordType}
            autoComplete="new-password"
            placeholder="Reenter password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            aria-invalid={mismatch}
            className="mt-1"
          />
          {mismatch && <p className="mt-1 text-xs text-red-600">The two don&rsquo;t match.</p>}
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="signup-show-password"
            checked={showPassword}
            onCheckedChange={(checked) => setShowPassword(checked === true)}
          />
          <Label htmlFor="signup-show-password" className="font-normal">
            Show password
          </Label>
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full gap-2" disabled={submitting || staff}>
          {submitting && <Loader2 size={14} className="animate-spin" />}
          Sign up
        </Button>
      </form>

      <p className="mt-4 text-center text-sm text-gray-700">
        Have an account already?{" "}
        <Link to="/login" className="font-medium underline underline-offset-2">
          Sign in
        </Link>
      </p>
    </AuthCard>
  )
}

export default Signup
